import api from '../lib/api'

export const ORG_TAGS_FETCH_LIMIT = 200

const orgTagsByTenant = new Map()
const orgTagsInflight = new Map()

export function parseTagListPayload(data) {
  if (Array.isArray(data)) return data
  if (data?.items && Array.isArray(data.items)) return data.items
  if (data?.tags && Array.isArray(data.tags)) return data.tags
  return []
}

export const listOrgTags = (tenantId, params = {}) =>
  api.get(`/manager/${tenantId}/tags`, { params }).then(r => parseTagListPayload(r.data))

/** Cached org tag list (200 items) per tenant — one network fetch until invalidate. */
export function peekCachedOrgTags(tenantId) {
  if (!tenantId) return null
  return orgTagsByTenant.get(tenantId) ?? null
}

export function invalidateOrgTagsCache(tenantId) {
  if (tenantId) {
    orgTagsByTenant.delete(tenantId)
    orgTagsInflight.delete(tenantId)
    return
  }
  orgTagsByTenant.clear()
  orgTagsInflight.clear()
}

export function appendCachedOrgTag(tenantId, tag) {
  if (!tenantId || !tag) return
  const list = orgTagsByTenant.get(tenantId)
  if (!list) return
  const id = tag.id ?? tag.tag_id
  if (id != null && list.some(t => t.id === id || t.tag_id === id)) return
  orgTagsByTenant.set(tenantId, [...list, tag])
}

export function replaceCachedOrgTag(tenantId, tag) {
  if (!tenantId || !tag) return
  const list = orgTagsByTenant.get(tenantId)
  if (!list) return
  const id = tag.id ?? tag.tag_id
  if (id == null) return
  orgTagsByTenant.set(
    tenantId,
    list.map(item => (item.id === id || item.tag_id === id ? { ...item, ...tag } : item)),
  )
}

export function removeCachedOrgTag(tenantId, tagId) {
  if (!tenantId || tagId == null) return
  const list = orgTagsByTenant.get(tenantId)
  if (!list) return
  orgTagsByTenant.set(
    tenantId,
    list.filter(item => item.id !== tagId && item.tag_id !== tagId),
  )
}

export function getCachedOrgTags(tenantId) {
  if (!tenantId) return Promise.resolve([])
  const cached = orgTagsByTenant.get(tenantId)
  if (cached) return Promise.resolve(cached)
  if (orgTagsInflight.has(tenantId)) return orgTagsInflight.get(tenantId)

  const promise = listOrgTags(tenantId, { skip: 0, limit: ORG_TAGS_FETCH_LIMIT })
    .then(tags => {
      orgTagsByTenant.set(tenantId, tags)
      orgTagsInflight.delete(tenantId)
      return tags
    })
    .catch(err => {
      orgTagsInflight.delete(tenantId)
      throw err
    })
  orgTagsInflight.set(tenantId, promise)
  return promise
}

export const createOrgTag = (tenantId, body) =>
  api.post(`/manager/${tenantId}/tags`, body).then(r => r.data)

export const updateOrgTag = (tenantId, tagId, body) =>
  api.patch(`/manager/${tenantId}/tags/${tagId}`, body).then(r => r.data)

export const deleteOrgTag = (tenantId, tagId) =>
  api.delete(`/manager/${tenantId}/tags/${tagId}`).then(r => r.data)

export const assignContactTag = (tenantId, contactId, tagId) =>
  api.post(`/manager/${tenantId}/contacts/${contactId}/tags/${tagId}`).then(r => r.data)

export const unassignContactTag = (tenantId, contactId, tagId) =>
  api.delete(`/manager/${tenantId}/contacts/${contactId}/tags/${tagId}`).then(r => r.data)
